import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Marker } from 'react-map-gl/mapbox';
import type { MapMouseEvent, MapRef } from 'react-map-gl/mapbox';
import { XMarkIcon, MapPinIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import MapEngine from '../mapping/MapEngine';
import { fairLendingApi } from '../../lib/api';
import { useTeamId } from '../../hooks/useTeamId';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
// Satellite with street labels: the user is looking for a building, not a road.
const SATELLITE_STYLE = 'mapbox://styles/mapbox/satellite-streets-v12';

type View = { longitude: number; latitude: number; zoom: number };

const US_VIEW: View = { longitude: -98.5795, latitude: 39.8283, zoom: 3.5 };

/**
 * Place a location by clicking it on the map — for an address that neither Census nor Geocodio can
 * find. The user never handles coordinates: the map opens near the address, they click the building,
 * and the server takes the census tract for that point.
 *
 * Centering the map uses Mapbox's geocoder for display only; nothing it returns is stored. What is
 * stored is the point the user clicked.
 */
export default function PlaceOnMapModal({
  branchId,
  branchName,
  addressLine,
  onClose,
}: {
  branchId: string;
  branchName: string;
  addressLine: string;
  onClose: () => void;
}) {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  // The map mounts at once, so its satellite style and tiles load WHILE the address is looked up;
  // it used to wait for the lookup before it started loading at all (Michael Wallace, 2026-10-14:
  // "sluggish"). An overlay hides it until it can jump straight to the address.
  const mapRef = useRef<MapRef>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [view, setView] = useState<View | null>(null);
  const [pin, setPin] = useState<{ longitude: number; latitude: number } | null>(null);

  useEffect(() => {
    if (!view || !mapLoaded) return;
    mapRef.current?.jumpTo({ center: [view.longitude, view.latitude], zoom: view.zoom });
  }, [view, mapLoaded]);

  useEffect(() => {
    let cancelled = false;
    const fallback = US_VIEW;
    if (!MAPBOX_TOKEN) {
      setView(fallback);
      return;
    }
    const url =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(addressLine)}.json` +
      `?country=us&limit=1&access_token=${MAPBOX_TOKEN}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (cancelled) return;
        const f = json?.features?.[0];
        if (!f?.center) return setView(fallback);
        // Street-level when the address itself matched, neighbourhood-level for a ZIP or city.
        const zoom = f.place_type?.includes('address') || f.place_type?.includes('poi') ? 17 : 13;
        setView({ longitude: f.center[0], latitude: f.center[1], zoom });
      })
      .catch(() => !cancelled && setView(fallback));
    return () => {
      cancelled = true;
    };
  }, [addressLine]);

  const placeMutation = useMutation({
    mutationFn: () => fairLendingApi.placeBranchOnMap(teamId!, branchId, pin!.latitude, pin!.longitude),
    onSuccess: (res) => {
      for (const key of ['branches', 'branch-aa-validation', 'fld-analysis', 'cra-assessment-areas', 'cra-dashboard']) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
      const tract: string | undefined = res.data?.branch?.censusTract;
      toast.success(tract ? `${branchName} placed — census tract ${tract}` : `${branchName} placed`);
      onClose();
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not place this location'),
  });

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Place on map — {branchName}</h3>
            <p className="text-sm text-gray-500">{addressLine}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-gray-700 mb-3">
          We couldn&rsquo;t find this address automatically. Click the building on the map, then save. Zoom
          and drag the map if it isn&rsquo;t in view.
        </p>

        <div className="relative h-[420px] rounded-lg overflow-hidden border border-gray-200">
          <MapEngine
            ref={mapRef}
            initialViewState={US_VIEW}
            mapStyle={SATELLITE_STYLE}
            onLoad={() => setMapLoaded(true)}
            onClick={(e: MapMouseEvent) => setPin({ longitude: e.lngLat.lng, latitude: e.lngLat.lat })}
          >
            {pin && (
              <Marker longitude={pin.longitude} latitude={pin.latitude} anchor="bottom">
                <MapPinIcon className="h-8 w-8 text-red-600 drop-shadow" />
              </Marker>
            )}
          </MapEngine>
          {!(view && mapLoaded) && (
            <div className="absolute inset-0 flex items-center justify-center bg-white text-sm text-gray-500">
              {view ? 'Loading map…' : 'Finding the address…'}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">
            {pin ? 'Pin placed. Click elsewhere to move it.' : 'No pin yet.'}
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900">
              Cancel
            </button>
            <button
              onClick={() => placeMutation.mutate()}
              disabled={!pin || placeMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-ocean-600 rounded-md hover:bg-ocean-700 disabled:opacity-50"
            >
              {placeMutation.isPending ? 'Saving…' : 'Save location'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
